/* VAMIRO product page: color-aware gallery synced with the BundleUp offer selectors. */
(function () {
  const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ');

  // "כחול" should match "כחול כהה" and vice versa, so match on a shared prefix.
  const colorsMatch = (a, b) => {
    a = normalize(a);
    b = normalize(b);
    return a !== '' && b !== '' && (a === b || a.startsWith(b) || b.startsWith(a));
  };

  class VmGallery extends HTMLElement {
    connectedCallback() {
      this.sets = Array.from(this.querySelectorAll('.vm-gallery__set'));
      this.sets.forEach((set) => this.setupSet(set));
      this.activeColor = this.sets[0] ? this.sets[0].dataset.color : '';
      this.syncGift();
    }

    setupSet(set) {
      const track = set.querySelector('.vm-gallery__track');
      const slides = Array.from(track.querySelectorAll('.vm-gallery__slide'));
      const thumbs = Array.from(set.querySelectorAll('.vm-gallery__thumb'));
      set.classList.toggle('vm-gallery__set--single', slides.length < 2);

      const currentIndex = () => Math.round(Math.abs(track.scrollLeft) / (track.clientWidth || 1));
      const goTo = (index) => {
        const clamped = Math.max(0, Math.min(slides.length - 1, index));
        // Scroll the track itself (not the page) so RTL negative scroll offsets are handled by the browser.
        const slideRect = slides[clamped].getBoundingClientRect();
        const trackRect = track.getBoundingClientRect();
        const target = track.scrollLeft + (slideRect.left - trackRect.left);
        track.scrollTo({ left: target, behavior: 'smooth' });
        // Some browsers cancel smooth scrolling on snap containers; make sure we land on the slide.
        clearTimeout(set.vmScrollCheck);
        set.vmScrollCheck = setTimeout(() => {
          if (Math.abs(track.scrollLeft - target) > 4) track.scrollTo({ left: target, behavior: 'instant' });
          markActive(clamped);
        }, 450);
      };
      const markActive = (index) => {
        thumbs.forEach((thumb, i) => {
          const active = i === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', String(active));
          if (active) {
            const strip = thumb.parentElement;
            const thumbRect = thumb.getBoundingClientRect();
            const stripRect = strip.getBoundingClientRect();
            if (thumbRect.left < stripRect.left || thumbRect.right > stripRect.right) {
              strip.scrollBy({ left: thumbRect.left - stripRect.left - stripRect.width / 2 + thumbRect.width / 2, behavior: 'smooth' });
            }
          }
        });
      };

      thumbs.forEach((thumb, i) => {
        thumb.setAttribute('aria-label', `${i + 1} / ${thumbs.length}`);
        thumb.addEventListener('click', () => goTo(i));
      });
      markActive(0);

      let ticking = false;
      track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            markActive(currentIndex());
            ticking = false;
          });
        },
        { passive: true }
      );

      set.querySelector('.vm-gallery__arrow--next').addEventListener('click', () => goTo(currentIndex() + 1));
      set.querySelector('.vm-gallery__arrow--prev').addEventListener('click', () => goTo(currentIndex() - 1));
    }

    showColor(color) {
      const target = this.sets.find((set) => colorsMatch(set.dataset.color, color));
      if (!target || colorsMatch(this.activeColor, target.dataset.color)) return;

      this.sets.forEach((set) => {
        const isTarget = set === target;
        set.classList.toggle('is-active', isTarget);
        set.hidden = !isTarget;
      });
      const track = target.querySelector('.vm-gallery__track');
      track.scrollLeft = 0;
      this.activeColor = target.dataset.color;
      this.syncGift();
    }

    // The free sport strap matches the chosen color, so mirror that color's sport image in the gift card.
    syncGift() {
      const giftImage = document.querySelector('.vm-gift__img');
      const active = this.sets.find((set) => !set.hidden);
      const sportSrc = active && active.dataset.sportSrc;
      if (!giftImage || !sportSrc) return;
      giftImage.src = sportSrc;
      giftImage.removeAttribute('srcset');
    }
  }

  if (!customElements.get('vm-gallery')) customElements.define('vm-gallery', VmGallery);

  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return String(format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return withCommas;
      if (key === 'amount_with_comma_separator') return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`;
      return `${withCommas}.${decimals}`;
    });
  };

  // Offer picker: one or two bracelets, a color each, plus a free matching sport strap per bracelet.
  class VmBundle extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.submitButton = this.querySelector('[data-submit]');
      this.errorEl = this.querySelector('.vm-bundle__error');
      this.markUnavailable();
      this.restoreColorNames();
      this.addEventListener('change', (event) => this.onChange(event));
      this.submitButton.addEventListener('click', () => this.submit());
      this.setupSticky();
      this.update();
    }

    // Floating buy bar: shown once the main button has scrolled off the top, hidden near the footer.
    // Uses a throttled scroll check so fast jumps (anchor links, flings) are never missed.
    setupSticky() {
      this.sticky = this.querySelector('[data-sticky]');
      if (!this.sticky) return;
      document.body.appendChild(this.sticky);
      this.stickyButton = this.sticky.querySelector('[data-sticky-submit]');
      this.stickyButton.addEventListener('click', () => this.submit());
      const footer = document.querySelector('footer');
      let queued = false;
      const render = () => {
        queued = false;
        const buttonPassed = this.submitButton.getBoundingClientRect().bottom < 0;
        const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        const show = buttonPassed && !footerVisible;
        if (show === this.sticky.classList.contains('is-visible')) return;
        this.sticky.classList.toggle('is-visible', show);
        document.body.classList.toggle('vm-sticky-on', show && window.innerWidth < 990);
        this.sticky.setAttribute('aria-hidden', String(!show));
        this.stickyButton.tabIndex = show ? 0 : -1;
      };
      const queue = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(render);
      };
      window.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue, { passive: true });
      render();
    }

    get quantity() {
      const checked = this.querySelector('.vm-offer__radio:checked');
      return checked ? parseInt(checked.value, 10) : 1;
    }

    colorFor(slot) {
      const checked = this.querySelector(`.vm-colors[data-slot="${slot}"] input:checked`);
      return checked ? checked.value : '';
    }

    displayFor(slot) {
      const checked = this.querySelector(`.vm-colors[data-slot="${slot}"] input:checked`);
      return checked ? checked.dataset.display || checked.value : '';
    }

    variantFor(color) {
      return this.variants.find((variant) => colorsMatch(variant.option1, color));
    }

    // On a translated storefront (e.g. /ar) Shopify translates the variant color names, so they no longer match the swatches.
    // Read the names in the store's main language (same variant ids) and use those instead.
    async restoreColorNames() {
      const inputs = Array.from(this.querySelectorAll('.vm-swatch input'));
      if (!inputs.length || inputs.some((input) => this.variantFor(input.value))) return;
      try {
        const response = await fetch(`${window.shopUrl || ''}/products/${this.dataset.productHandle}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const original = await response.json();
        this.variants = this.variants.map((variant) => {
          const match = original.variants.find((item) => item.id === variant.id);
          return match ? { ...variant, option1: match.option1 } : variant;
        });
        this.markUnavailable();
      } catch (error) {
        /* keep the current state */
      }
    }

    markUnavailable() {
      this.querySelectorAll('.vm-swatch input').forEach((input) => {
        const variant = this.variantFor(input.value);
        const unavailable = !variant || !variant.available;
        input.disabled = unavailable;
        input.closest('.vm-swatch').classList.toggle('is-unavailable', unavailable);
      });
    }

    onChange(event) {
      const colorFieldset = event.target.closest('.vm-colors');
      this.update();
      if (colorFieldset) {
        const gallery = document.querySelector('vm-gallery');
        if (gallery && typeof gallery.showColor === 'function') gallery.showColor(event.target.value);
      }
    }

    update() {
      const pair = this.quantity === 2;
      this.querySelectorAll('.vm-colors').forEach((fieldset) => {
        const slot = fieldset.dataset.slot;
        if (slot === '2') fieldset.hidden = !pair;
        const checked = fieldset.querySelector('input:checked');
        fieldset.querySelector('[data-color-name]').textContent = (checked && checked.dataset.display) || this.colorFor(slot);
        const label = fieldset.querySelector('[data-slot-label]');
        label.dataset.base = label.dataset.base || label.textContent;
        label.textContent = pair ? `${label.dataset.base} – ${this.dataset.slotLabel} ${slot}` : label.dataset.base;
      });
      this.querySelectorAll('.vm-offer').forEach((offer) => {
        offer.classList.toggle('is-selected', offer.querySelector('.vm-offer__radio').checked);
      });
      const price = pair ? this.dataset.pairPrice : this.dataset.unitPrice;
      const formatted = formatMoney(parseInt(price, 10), this.dataset.moneyFormat);
      this.querySelector('[data-submit-label]').textContent = `${this.dataset.buttonText} – ${formatted}`;
      const stickyPrice = this.sticky && this.sticky.querySelector('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = formatted;
      this.showError('');
    }

    buildItems() {
      const lines = new Map();
      const add = (id, properties) => {
        const key = `${id}|${JSON.stringify(properties || {})}`;
        const line = lines.get(key) || { id, quantity: 0, properties };
        line.quantity += 1;
        lines.set(key, line);
      };
      for (let slot = 1; slot <= this.quantity; slot++) {
        const color = this.colorFor(slot);
        const variant = this.variantFor(color);
        if (!variant || !variant.available) return null;
        add(variant.id);
        if (this.dataset.giftVariant) {
          const giftProperties = document.documentElement.classList.contains('vm-lang-ar')
            ? { '_צבע': color, [this.dataset.giftPropertyAr || 'اللون']: this.displayFor(slot) }
            : { [this.dataset.giftProperty || 'צבע']: color };
          add(parseInt(this.dataset.giftVariant, 10), giftProperties);
        }
      }
      return Array.from(lines.values()).map((line) => (line.properties ? line : { id: line.id, quantity: line.quantity }));
    }

    showError(message) {
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
      if (message && this.sticky && this.sticky.classList.contains('is-visible')) {
        this.errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    async submit() {
      if (this.submitButton.classList.contains('is-loading')) return;
      const items = this.buildItems();
      if (!items) {
        this.showError(this.dataset.errorText);
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const body = { items };
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      this.submitButton.classList.add('is-loading');
      if (this.stickyButton) this.stickyButton.classList.add('is-loading');
      this.submitButton.setAttribute('aria-disabled', 'true');
      this.showError('');

      try {
        const response = await fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          this.showError(state.description || state.message || this.dataset.errorText);
          return;
        }
        if (cartDrawer && state.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents({ ...state, id: state.items && state.items[0] ? state.items[0].product_id : null });
        } else {
          window.location.href = window.routes ? window.routes.cart_url : '/cart';
        }
      } catch (error) {
        this.showError(this.dataset.errorText);
      } finally {
        this.submitButton.classList.remove('is-loading');
        if (this.stickyButton) this.stickyButton.classList.remove('is-loading');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }
  }

  if (!customElements.get('vm-bundle')) customElements.define('vm-bundle', VmBundle);

  // Short Q&A: keep a single answer open at a time.
  document.addEventListener(
    'toggle',
    (event) => {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.classList.contains('vm-qa__item') || !item.open) return;
      item.closest('.vm-qa').querySelectorAll('.vm-qa__item[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    },
    true
  );

  // Honest urgency: a real sale end date (set in the editor) and real low stock from Shopify inventory.
  class VmUrgency extends HTMLElement {
    connectedCallback() {
      try {
        this.stock = JSON.parse(this.querySelector('[data-stock]').textContent);
      } catch (e) {
        this.stock = {};
      }
      this.renderSale();
      this.renderStock();
      document.addEventListener('change', (event) => {
        if (event.target.closest('vm-bundle .vm-colors[data-slot="1"]')) this.renderStock();
      });
    }

    renderSale() {
      const line = this.querySelector('[data-sale]');
      const raw = (this.dataset.saleEnd || '').trim();
      if (!line || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return;
      const [y, m, d] = raw.split('-').map(Number);
      const end = new Date(y, m - 1, d, 23, 59, 59);
      const now = new Date();
      if (end < now) return;
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const days = Math.round((new Date(y, m - 1, d) - today) / 86400000);
      const hours = Math.max(1, Math.ceil((end - now) / 3600000));
      line.querySelector('span').textContent =
        days === 0
          ? this.dataset.saleLastDay
          : this.dataset.saleText.replace('[days]', String(days)).replace('[hours]', String(hours));
      line.hidden = false;
    }

    renderStock() {
      const line = this.querySelector('[data-stock-line]');
      const threshold = parseInt(this.dataset.lowStock, 10);
      if (!line || !threshold) return;
      const checked = document.querySelector('vm-bundle .vm-colors[data-slot="1"] input:checked');
      const color = checked ? checked.value : Object.keys(this.stock)[0];
      const key = Object.keys(this.stock).find((name) => colorsMatch(name, color));
      const qty = key ? this.stock[key].qty : null;
      const show = typeof qty === 'number' && qty > 0 && qty <= threshold;
      line.hidden = !show;
      if (show) {
        line.querySelector('span:last-child').textContent = this.dataset.lowStockText
          .replace('[count]', String(qty))
          .replace('[color]', key);
      }
    }
  }

  if (!customElements.get('vm-urgency')) customElements.define('vm-urgency', VmUrgency);

  // Read the color of the first item in the selected BundleUp offer (falls back to the theme's variant inputs).
  const readSelectedColor = () => {
    const ownSwatch = document.querySelector('vm-bundle .vm-colors[data-slot="1"] input:checked');
    if (ownSwatch) return ownSwatch.value;

    // BundleUp keeps a "selectedBU" class on its featured offer, so the checked radio is the real selection.
    const checked = document.querySelector('.bu-offer-radio:checked');
    const card = checked && checked.closest('.bundle-offer-card');
    const selected =
      (card && card.querySelector('.bundle-item-option-set[data-item-index="0"] .bu-selected-option')) ||
      document.querySelector('.bundle-item-option-set[data-item-index="0"] .bu-selected-option');
    if (selected && selected.dataset.value) return selected.dataset.value;

    const input = document.querySelector('product-info variant-selects input:checked, product-info variant-selects select');
    return input ? input.value : '';
  };

  const syncColor = () => {
    const gallery = document.querySelector('vm-gallery');
    if (!gallery || typeof gallery.showColor !== 'function') return;
    const color = readSelectedColor();
    if (color) gallery.showColor(color);
  };

  let pending = null;
  const scheduleSync = () => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = null;
      syncColor();
    }, 60);
  };

  const start = () => {
    if (!document.querySelector('vm-gallery')) return;
    // Our own offer picker drives the gallery directly; the observer is only needed for BundleUp.
    if (document.querySelector('vm-bundle')) return;
    // BundleUp renders after load and rewrites its markup on every selection, so watch for changes.
    new MutationObserver(scheduleSync).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-value', 'class', 'checked'],
    });
    document.addEventListener('change', scheduleSync);
    document.addEventListener('click', scheduleSync);
    syncColor();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
